import { useMemo, useCallback, useState, useEffect } from 'react';
import {
  PERMISSIONS,
  MODULES,
  SUB_ROLES,
  hasPermission as checkPermission,
  canAccessModule as checkModuleAccess,
  canAccessRoute as checkRouteAccess,
  getAccessibleModules,
  canValidateBatch as checkCanValidateBatch,
  canValidateBatchByStatus,
  getDefaultRouteForRole,
  getPermissionsForRole,
} from '../config/permissions';

/**
 * Custom hook for managing user permissions
 * 
 * This hook provides a convenient interface for checking user permissions
 * throughout the application. It reads the user's role from localStorage
 * and provides methods to check various permissions.
 * 
 * @returns {Object} Permission checking utilities
 * 
 * @example
 * const { hasPermission, canAccess, canValidate, role } = usePermissions();
 * 
 * // Check specific permission
 * if (hasPermission(PERMISSIONS.CAN_VALIDATE_BATCH)) { ... }
 * 
 * // Check module access
 * if (canAccess.module(MODULES.DATA_PREVIEW)) { ... }
 * 
 * // Check if user can validate a specific batch
 * if (canValidate(batch.status)) { ... }
 */
const usePermissions = () => {
  // Get current user role from localStorage - use useState to make it reactive
  const [role, setRole] = useState(() => {
    const storedRole = localStorage.getItem('userRole');
    return storedRole ? storedRole.toLowerCase() : null;
  });

  // Get sub-roles from localStorage - use useState to make it reactive
  const [subRoles, setSubRoles] = useState(() => {
    try {
      const storedSubRoles = localStorage.getItem('userSubRoles');
      if (storedSubRoles) {
        const parsed = JSON.parse(storedSubRoles);
        return Array.isArray(parsed) ? parsed.map(sr => sr?.toLowerCase()) : [];
      }
    } catch (e) {
      console.warn('Failed to parse userSubRoles from localStorage:', e);
    }
    return [];
  });

  // Listen for localStorage changes (e.g., when user logs in/out)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'userRole' || e.key === 'userSubRoles' || !e.key) {
        const storedRole = localStorage.getItem('userRole');
        setRole(storedRole ? storedRole.toLowerCase() : null);
        
        try {
          const storedSubRoles = localStorage.getItem('userSubRoles');
          if (storedSubRoles) {
            const parsed = JSON.parse(storedSubRoles);
            setSubRoles(Array.isArray(parsed) ? parsed.map(sr => sr?.toLowerCase()) : []);
          } else {
            setSubRoles([]);
          }
        } catch (e) {
          console.warn('Failed to parse userSubRoles from localStorage:', e);
          setSubRoles([]);
        }
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Get all permissions for current user (including sub-role permissions)
  const permissions = useMemo(() => {
    return getPermissionsForRole(role, subRoles);
  }, [role, subRoles]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    if (!role) return false;
    return checkPermission(role, permission, subRoles);
  }, [role, subRoles]);

  // Check if user can access a specific module
  const canAccessModule = useCallback((module) => {
    if (!role) return false;
    return checkModuleAccess(role, module, subRoles);
  }, [role, subRoles]);

  // Check if user can access a specific route
  const canAccessRoute = useCallback((routePath) => {
    if (!role) return false;
    return checkRouteAccess(role, routePath, subRoles);
  }, [role, subRoles]);

  // Get all accessible modules for current user
  const accessibleModules = useMemo(() => {
    if (!role) return [];
    return getAccessibleModules(role);
  }, [role]);

  // Check if user can validate a batch (combines permission and status)
  const canValidate = useCallback((batchStatus) => {
    if (!role) return false;
    return checkCanValidateBatch(role, batchStatus, subRoles);
  }, [role, subRoles]);

  // Check if user has validate permission (regardless of batch status)
  const hasValidatePermission = useMemo(() => {
    return hasPermission(PERMISSIONS.CAN_VALIDATE_BATCH);
  }, [hasPermission]);

  // Get default route for current user based on role
  const defaultRoute = useMemo(() => {
    if (!role) return '/';
    return getDefaultRouteForRole(role);
  }, [role]);

  // Convenience object for module access checks
  const canAccess = useMemo(() => ({
    module: canAccessModule,
    route: canAccessRoute,
    taskStatus: canAccessModule(MODULES.TASK_STATUS),
    auditTrail: canAccessModule(MODULES.AUDIT_TRAIL),
    search: canAccessModule(MODULES.SEARCH),
    dataPreview: canAccessModule(MODULES.DATA_PREVIEW),
    visualization: canAccessModule(MODULES.VISUALIZATION),
    validateData: canAccessModule(MODULES.VALIDATE_DATA),
    validateMapping: canAccessModule(MODULES.VALIDATE_MAPPING),
  }), [canAccessModule, canAccessRoute]);

  // Check if user is authenticated (has a role)
  const isAuthenticated = useMemo(() => {
    return !!role && !!localStorage.getItem('token');
  }, [role]);

  // Check if user has viewer sub-role (can see Validate button)
  const hasViewerSubRole = useMemo(() => {
    return subRoles.includes(SUB_ROLES.VIEWER);
  }, [subRoles]);

  // Check if user has reviewer sub-role
  const hasReviewerSubRole = useMemo(() => {
    return subRoles.includes(SUB_ROLES.REVIEWER);
  }, [subRoles]);

  return {
    // Current user role
    role,
    
    // User's sub-roles
    subRoles,
    hasViewerSubRole,
    hasReviewerSubRole,
    
    // User's permissions array
    permissions,
    
    // Permission check functions
    hasPermission,
    canAccessModule,
    canAccessRoute,
    canValidate,
    
    // Check if can validate batch by status only (without permission check)
    canValidateByStatus: canValidateBatchByStatus,
    
    // Pre-computed values
    accessibleModules,
    hasValidatePermission,
    defaultRoute,
    isAuthenticated,
    
    // Convenience access object
    canAccess,
    
    // Export permission and module constants for convenience
    PERMISSIONS,
    MODULES,
    SUB_ROLES,
  };
};

export default usePermissions;

